import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, AlertTriangle, XCircle, Info, X, Bell
} from 'lucide-react';
import { ToastNotification } from '../types';

interface ToastNotificationsProps {
  notifications: ToastNotification[];
  onDismiss: (id: string) => void;
}

const ToastNotifications: React.FC<ToastNotificationsProps> = ({ notifications, onDismiss }) => {
  const getToastIcon = (type: string) => {
    switch (type) {
      case 'success':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'error':
        return XCircle;
      default:
        return Info;
    }
  };

  const getToastColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-primary/10 border-primary/30 text-primary-dark';
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-primary';
    }
  };

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map((notification) => {
        const IconComponent = getToastIcon(notification.type);
        
        return (
          <ToastItem
            key={notification.id}
            notification={notification}
            IconComponent={IconComponent}
            toastColor={getToastColor(notification.type)}
            iconColor={getIconColor(notification.type)}
            onDismiss={onDismiss}
          />
        );
      })}
    </div>
  );
};

interface ToastItemProps {
  notification: ToastNotification;
  IconComponent: React.ComponentType<any>;
  toastColor: string;
  iconColor: string;
  onDismiss: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ 
  notification, 
  IconComponent, 
  toastColor, 
  iconColor, 
  onDismiss 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    
    // Auto dismiss
    if (notification.duration && notification.duration > 0) {
      const dismissTimer = setTimeout(() => {
        handleDismiss();
      }, notification.duration);
      
      return () => {
        clearTimeout(timer);
        clearTimeout(dismissTimer);
      };
    }
    
    return () => clearTimeout(timer);
  }, [notification.duration]);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(notification.id);
    }, 300);
  };

  return (
    <div
      className={`transform transition-all duration-300 ease-in-out ${
        isVisible && !isLeaving 
          ? 'translate-x-0 opacity-100' 
          : 'translate-x-full opacity-0'
      }`}
    >
      <div className={`border rounded-lg shadow-lg p-4 ${toastColor}`}>
        <div className="flex items-start">
          <div className={`flex-shrink-0 ${iconColor}`}>
            <IconComponent className="w-5 h-5" />
          </div>
          
          <div className="ml-3 flex-1">
            <h4 className="text-sm font-medium">
              {notification.title}
            </h4>
            <p className="text-sm mt-1 opacity-90">
              {notification.message}
            </p>
            
            {notification.action && (
              <button
                onClick={notification.action.onClick}
                className="mt-2 text-sm font-medium underline hover:no-underline"
              >
                {notification.action.text}
              </button>
            )}
          </div>
          
          {notification.dismissible !== false && (
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToastNotifications;